import { Button, Input, Switch } from "@omnabase/ui";
import { FORM_SLUG_MAX, formPageUrl } from "@omnabase/shared";
import type { OpenProject } from "../../../lib/formEdits";

/** the shipped help article on the snippet, the address and prefill by link (round two, served by the API at /docs/<name>) */
export const EMBED_ARTICLE = "/docs/forms-on-your-site";

/** "Where submissions go" (the form builder, round one; the brief's last section, question 15; builder v2: the body of its row): the default project ("Submissions go to") and
 *  the Published switch, which waits until the form knows its project (a default, or a Project field); a published form keeps that way. */
export function FormWhereSection({ defaultProjectId, published, projects, problem, edit, onDefault, onPublished }: {
  defaultProjectId: string | null; published: boolean; projects: readonly OpenProject[]; /** why the form cannot be published yet (null: it can) */ problem: string | null; edit: boolean;
  onDefault: (id: string | null) => void; onPublished: (on: boolean) => void;
}) {
  const known = !defaultProjectId || projects.some((p) => p.id === defaultProjectId);
  return (
    <div className="ob-form-builder__row-settings">
      <div className="ob-form-builder__setting">
        <label className="ob-field-label" htmlFor="ob-fb-default">Submissions go to</label>
        <select id="ob-fb-default" className="ob-select" aria-label="Submissions go to" value={defaultProjectId ?? ""} disabled={!edit} onChange={(e) => onDefault(e.target.value || null)}>
          <option value="">No default</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          {!known && <option value={defaultProjectId!}>A project no longer open</option>}
        </select>
        <p className="ob-form-builder__hint">A Project field in the form lets the person choose instead.</p>
      </div>
      <label className="ob-form-builder__toggle"><Switch label="Published" checked={published} disabled={!edit || (!published && !!problem)} onChange={onPublished} />Published</label>
      {problem ? <p className={published ? "ob-form-builder__problem" : "ob-form-builder__hint"}>{problem}</p> : <p className="ob-form-builder__hint">{published ? "Live: its page and every embed take submissions." : "A draft: nobody can use it yet."}</p>}
    </div>
  );
}

/** "Share" (the brief's "Also in round one"; builder v2: the body of its row): the form's own address with Copy, and its slug, editable; the page answers once the form is
 *  published (round two) */
export function FormShareSection({ workspaceSlug, slug, problem, edit, onSlug, onCopy }: { workspaceSlug: string; slug: string; problem: string | null; edit: boolean; onSlug: (slug: string) => void; onCopy: (address: string) => void }) {
  const address = formPageUrl(workspaceSlug, slug);
  return (
    <div className="ob-form-builder__row-settings">
      <div className="ob-form-builder__address"><code className="ob-form-builder__url">{address}</code><Button size="sm" icon="copy" onClick={() => onCopy(address)}>Copy</Button></div>
      <div className="ob-form-builder__setting">
        <label className="ob-field-label" htmlFor="ob-fb-slug">Address</label>
        <Input id="ob-fb-slug" className="ob-input--text" prefix="/forms/" value={slug} maxLength={FORM_SLUG_MAX + 10} disabled={!edit} onChange={(e) => onSlug(e.target.value)} />
        <p className={problem ? "ob-form-builder__problem" : "ob-form-builder__hint"}>{problem ?? <>Anyone with this address can fill in the form once it is published. <a className="ob-link" href={EMBED_ARTICLE} target="_blank" rel="noopener noreferrer">Put it on your own site</a></>}</p>
      </div>
    </div>
  );
}
